import { format } from "date-fns";
import add from "date-fns/add";
import getMonth from "date-fns/getMonth";
import getYear from "date-fns/getYear";
import isBefore from "date-fns/isBefore";
import { saveAs } from "file-saver";
import _, { camelCase, kebabCase, mapKeys } from "lodash";

import { toUTC } from "./DateAdapter";

export const saveAsCsv = (csv, filename) => {
  const blob = new Blob([csv], {
    type: "text/csv;charset=utf-8",
  });
  saveAs(blob, filename);
};

export const displayId = (itemNumber, rn) =>
  rn > 1 ? itemNumber + "." + rn : itemNumber;

export const diff = (a, b) => {
  const r = {};
  _.each(a, function (v, k) {
    if (_.isEqual(b[k], v)) return;
    // but what if it returns an empty object? still attach?
    r[k] = [v, b[k]];
  });
  return r;
};

export const sorted = (arr) => {
  return _.sortBy(arr, (v) => v);
};

export const kebabCaseKeys = (obj) => mapKeys(obj, (_, k) => kebabCase(k));
export const camelCaseKeys = (obj) => mapKeys(obj, (_, k) => camelCase(k));

export const camelCaseKeysDeep = (obj) => renameKeysDeep(obj, camelCase);
export const kebabCaseKeysDeep = (obj) => renameKeysDeep(obj, kebabCase);

function renameKeysDeep(obj, keyMapper) {
  if (Array.isArray(obj)) {
    return obj.map((item) => renameKeysDeep(item, keyMapper));
  }
  if (!_.isPlainObject(obj)) return obj;

  return Object.keys(obj).reduce((acc, key) => {
    const newKey = keyMapper(key);
    // protect against prototype pollution
    if (
      newKey === "__proto__" ||
      newKey === "constructor" ||
      newKey === "prototype"
    ) {
      return acc;
    }
    acc[newKey] = renameKeysDeep(obj[key], keyMapper);
    return acc;
  }, {});
}

export const upCase = (string, splitter) => {
  if (!string) return "";
  return string
    .split(splitter || " ")
    .map((s) => s[0].toUpperCase() + s.slice(1))
    .join(" ");
};

//Handles filtering of programs for the Pre-Order Program View
export const filter = (array, filters) => {
  const filteredArray = [];
  if (filters.length !== 0) {
    array.forEach((item) => {
      let filtered = true;
      for (let i = 0; i < filters.length; i++) {
        if (
          filters[i].type === "brand" &&
          !item.brand.includes(filters[i].value.replace(/^\w+\s/, "")) // removes the item code from the value before comparing
        ) {
          filtered = false;
          break;
        }
        if (
          filters[i].type === "unit" &&
          !item.unit.includes(filters[i].value.name)
        ) {
          filtered = false;
          break;
        }
        if (
          item[filters[i].type] !== filters[i].value &&
          filters[i].type !== "brand" &&
          filters[i].type !== "unit"
        ) {
          filtered = false;
          break;
        }
      }
      if (filtered) {
        filteredArray.push(item);
      }
    });
    return filteredArray;
  } else return array;
};

export const earliestDate = (dateArray) => {
  const sortedDates = dateArray.sort((a, b) => {
    return isBefore(new Date(a["start-date"]), new Date(b["start-date"]))
      ? -1
      : !isBefore(new Date(a["start-date"]), new Date(b["start-date"]))
        ? 1
        : 0;
  });
  return sortedDates;
};

const USDollar = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export const formatMoney = (value, ops) =>
  formatMoneyCents(isNaN(value) ? 0 : value, ops ? 4 : 2);

export const formatMoneyCents = (value, decimals = 2) =>
  `$${parseFloat(value / 100).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;

const maxFractionDigitFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 10,
  minimumFractionDigits: 4,
});
export const formatMoneyUnlimitedDecimals = (value) => {
  return maxFractionDigitFormatter.format(parseFloat(value));
};

export const formatMoneyNDecimals = (value, decimals = 2) =>
  parseFloat(value).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

export const formatMoneyString = (value) => USDollar.format(value ?? 0);

//handles incoming money values that are strings
export const stringToCents = (numString) => {
  return parseFloat(numString) * 100;
};

//Used in order tables to ensure order numbers match pack size
export const roundUp = (value, rounder) => {
  if (rounder === 0) return String(value);
  return String(Math.ceil(value / rounder) * rounder);
};

//Used for filtering purposes, just a quick call to modify an array
export const separateByComma = (array, key) => {
  if (!array || !array.length) return null;
  if (key) {
    return array.map((index) => index[key]).join(",");
  } else {
    return array.join(",");
  }
};

//Builds a useable array in the query string
export const buildQueryArray = (filter, arg, key) => {
  if (typeof arg !== "object") {
    return `filter[${filter}]%5B%5D=${arg}`;
  } else {
    if (key && typeof arg[0] === "object") {
      return arg
        .map((index) => `filter[${filter}]%5B%5D=${index[key]}`)
        .join("&");
    } else {
      return arg.map((a) => `filter[${filter}]%5B%5D=${a}`).join("&");
    }
  }
};

export const utcDate = (dateString) => toUTC(new Date(dateString));
export const formatUtcDate = (dateString, formatString = "MM/dd/yyyy") =>
  format(utcDate(dateString), formatString);

//adds shifted time in pickers so utc timestamps show up correctly
export const formatDate = (dateString) => {
  const initialDate = new Date(dateString);
  // Super hacky!!!!
  return add(initialDate, { hours: 8 });
};

export const formatDateString = (date) => {
  if (!date) return date;
  const dateArray = date.split("-");
  return `${dateArray[1]}/${dateArray[2]}/${dateArray[0]}`;
};

export const monthMap = {
  1: "January",
  2: "February",
  3: "March",
  4: "April",
  5: "May",
  6: "June",
  7: "July",
  8: "August",
  9: "September",
  10: "October",
  11: "November",
  12: "December",
};

//generates order window selections for filters
export const getWindowSelections = (programType) => {
  const date = new Date();
  const currentMonth = getMonth(date);
  const currentYear = getYear(date);
  const futureMonthsToInclude = 5;
  let month = currentMonth + futureMonthsToInclude;
  if (month > 11) month -= 12;
  let year =
    currentMonth + futureMonthsToInclude > 11 ? currentYear + 1 : currentYear;

  const dateArray = [];
  // Generate options down to Jan of last year
  for (let i = 0; i < 13 + futureMonthsToInclude + currentMonth; i++) {
    dateArray.push({
      month: month + 1,
      year: year,
      programType: programType,
    });
    month -= 1;
    if (month === -1) {
      month = 11;
      year -= 1;
    }
  }
  return dateArray;
};

// Shifts an object by a value for a key to the front of an array
export const shiftToFront = (key, value, arr) => {
  const isEqualToValue = (x) => x[key] === value;
  const idx = arr.findIndex(isEqualToValue);
  if (idx !== -1) {
    const a = arr.splice(idx, 1);
    arr.unshift(a[0]);
  }
};
